import { useEffect, useMemo, useState } from 'react';

import { queryTemplateApi } from '@/services/api/queryTemplateApi';

const QUERY_TEMPLATE_ID_REGEX = /^[a-f0-9]{24}$/i;

function normalizeRows(rows: Record<string, unknown>[]): Record<string, unknown>[] {
  return rows.map((row) => {
    if (row._id && typeof row._id === 'object' && !Array.isArray(row._id)) {
      const { _id, ...rest } = row;
      return { ...rest, ...(_id as Record<string, unknown>) };
    }
    return row;
  });
}

export function isQueryTemplateDependencyId(id: string | undefined | null): boolean {
  if (!id) return false;
  return QUERY_TEMPLATE_ID_REGEX.test(id);
}

export function useComputedExpressionDependencies(dependencyIds: string[]): {
  dependencyData: Record<string, Record<string, unknown>[]>;
  dependencyErrors: Record<string, string>;
  allLoaded: boolean;
} {
  const templateIds = useMemo(
    () => [...new Set(dependencyIds.filter(isQueryTemplateDependencyId))],
    [dependencyIds],
  );
  const [dependencyData, setDependencyData] = useState<Record<string, Record<string, unknown>[]>>({});
  const [dependencyErrors, setDependencyErrors] = useState<Record<string, string>>({});
  const [allLoaded, setAllLoaded] = useState(templateIds.length === 0);

  useEffect(() => {
    let cancelled = false;

    if (templateIds.length === 0) {
      setDependencyData({});
      setDependencyErrors({});
      setAllLoaded(true);
      return () => {
        cancelled = true;
      };
    }

    setAllLoaded(false);
    setDependencyErrors({});

    void Promise.all(
      templateIds.map(async (templateId) => {
        try {
          const result = await queryTemplateApi.execute(templateId, {}, { skipCache: true });
          return { templateId, rows: normalizeRows(result.data || []) };
        } catch (error) {
          return {
            templateId,
            rows: [],
            error: error instanceof Error ? error.message : String(error),
          };
        }
      }),
    ).then((results) => {
      if (cancelled) return;
      setDependencyData(Object.fromEntries(results.map(result => [result.templateId, result.rows])));
      setDependencyErrors(Object.fromEntries(
        results
          .filter(result => Boolean(result.error))
          .map(result => [result.templateId, result.error!]),
      ));
      setAllLoaded(true);
    });

    return () => {
      cancelled = true;
    };
  }, [templateIds]);

  return { dependencyData, dependencyErrors, allLoaded };
}
